import { useCallback, useMemo } from 'react'
import { collection, query, where, doc, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { addDoc, updateDoc, deleteDoc } from '../lib/firestore'
import { runMutation } from '../lib/firestoreMutation'
import { useFirestoreCollection } from './useFirestore'
import { useResetOnChange } from './useResetOnChange'
import { compareStageOrder } from '../lib/utils'
import { track } from '../lib/analytics'
import type { ScheduleSettings, Stage } from '../types'
import { fbt } from 'fbtee'

export function useScheduleSettings(
  userId: string | null,
  eventId: string,
  stages: Stage[],
) {
  const q = useMemo(
    () =>
      userId
        ? (query(
            collection(db, 'scheduleSettings'),
            where('userId', '==', userId),
            where('eventId', '==', eventId),
          ) as Query<ScheduleSettings>)
        : null,
    [userId, eventId],
  )

  const { data } = useFirestoreCollection(q)
  const settings = data.length > 0 ? data[0] : null

  const firestoreOrderKey = settings?.stageOrder?.join(',') ?? ''
  const firestoreHiddenKey = settings?.hiddenStages?.join(',') ?? ''

  // Firestore has caught up with (or overridden) the optimistic value — drop it
  // and fall back to the stored order.
  const [optimisticOrder, setOptimisticOrder] =
    useResetOnChange<string[] | null>(firestoreOrderKey, () => null)
  const [optimisticHidden, setOptimisticHidden] =
    useResetOnChange<string[] | null>(firestoreHiddenKey, () => null)

  const adminSorted = useMemo(() => [...stages].sort(compareStageOrder), [stages])

  const orderedStages = useMemo(() => {
    const stageOrder = optimisticOrder ?? settings?.stageOrder
    if (!stageOrder) return adminSorted

    const stageMap = new Map(adminSorted.map(s => [s.id, s]))
    const result: Stage[] = []
    const seen = new Set<string>()

    for (const id of stageOrder) {
      const stage = stageMap.get(id)
      if (stage) {
        result.push(stage)
        seen.add(id)
      }
    }

    for (const stage of adminSorted) {
      if (!seen.has(stage.id)) result.push(stage)
    }

    return result
  }, [optimisticOrder, settings, adminSorted])

  const hiddenStageIds = useMemo(
    () => new Set(optimisticHidden ?? settings?.hiddenStages ?? []),
    [optimisticHidden, settings],
  )

  const visibleStages = useMemo(
    () => orderedStages.filter(s => !hiddenStageIds.has(s.id)),
    [orderedStages, hiddenStageIds],
  )

  const allStagesWithVisibility = useMemo(
    () => orderedStages.map(s => ({ ...s, hidden: hiddenStageIds.has(s.id) })),
    [orderedStages, hiddenStageIds],
  )

  const persist = useCallback(
    async (stageOrder: string[], hiddenStages: string[]) => {
      if (!userId) return
      await runMutation(fbt('schedule settings', 'Error toast when saving schedule stage order or visibility fails'), async () => {
        if (settings) {
          await updateDoc(doc(db, 'scheduleSettings', settings.id), {
            stageOrder,
            hiddenStages,
            updatedAt: serverTimestamp(),
          })
        } else {
          await addDoc(collection(db, 'scheduleSettings'), {
            userId,
            eventId,
            stageOrder,
            hiddenStages,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        }
      })
    },
    [userId, eventId, settings],
  )

  const updateStageOrder = useCallback(
    (stageIds: string[]) => {
      setOptimisticOrder(stageIds)
      track('schedule_settings_changed', { action: 'reorder', event_id: eventId })
      persist(stageIds, optimisticHidden ?? settings?.hiddenStages ?? [])
    },
    [persist, settings, optimisticHidden, eventId, setOptimisticOrder],
  )

  const toggleStageVisibility = useCallback(
    (stageId: string) => {
      const currentHidden = new Set(optimisticHidden ?? settings?.hiddenStages ?? [])
      const action = currentHidden.has(stageId) ? 'unhide' : 'hide'
      if (action === 'unhide') currentHidden.delete(stageId)
      else currentHidden.add(stageId)
      const newHidden = [...currentHidden]
      setOptimisticHidden(newHidden)
      track('schedule_settings_changed', { action, event_id: eventId })
      persist(
        optimisticOrder ?? settings?.stageOrder ?? orderedStages.map(s => s.id),
        newHidden,
      )
    },
    [persist, settings, orderedStages, optimisticOrder, optimisticHidden, eventId, setOptimisticHidden],
  )

  const resetToDefault = useCallback(async () => {
    if (!settings) return
    setOptimisticOrder(null)
    setOptimisticHidden(null)
    track('schedule_settings_changed', { action: 'reset', event_id: eventId })
    await runMutation(fbt('schedule settings', 'Error toast when saving schedule stage order or visibility fails'), () =>
      deleteDoc(doc(db, 'scheduleSettings', settings.id)),
    )
  }, [settings, eventId, setOptimisticOrder, setOptimisticHidden])

  return {
    visibleStages,
    allStagesWithVisibility,
    updateStageOrder,
    toggleStageVisibility,
    resetToDefault,
    hasCustomSettings: settings !== null,
  }
}
